import styles from "./LeftSection.module.scss";
import PropertyTopSection from "../PropertyTopSection";
import OffersSection from "../OffersSection";
import PropertyAbout from "../PropertyAboutSection";
import SmartQuestions, { getTwoQuestionSets } from "../SmartQuestions";
import VirtualTourConnectSection from "../VirtualTourConnectSection";
import AmenitiesSection from "../AmenitiesSection";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import SectionRegionInfo from "@Pages/Search/components/SectionRegionInfo";
import RoomTypeContextProvider from "../RoomTypeSection/context";
import { LocationProvider } from "../MapSection/locationContext";
import { lazy, useMemo } from "react";
import SectionFeaturedDesktop from "../SectionFeaturedDesktop";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import NextRoomButton from "../NextRoomButton";
import SimilarPropertiesWrapper from "@Pages/Search/components/SimilarProperties/SimilarPropertiesWrapper";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  GROWTH_BOOK_FEATURE_FLAG_NAMES,
  HIDE_OFFERS_FOR_WL_INVENTORY_PARTNER_IDS,
} from "constants/index";
import RoomTypeSectionDynamicDesktop from "../RoomTypeSection/RoomTypeSectionDynamicDesktop";
import VideoSection from "../VideoSection";
import loadable from "@loadable/component";
import FullPageLoader from "@Components/FullPageLoader";
import HouseRules from "../HouseRules";
import PaymentAndFees from "../PaymentAndFees";
import MeetTheResidents from "../MeetTheResidents";
import FloorPlansSection from "../FloorPlansSection";
import { isWhitelabelPartnerInventoryFunc } from "@Utils/whitelabelHelper";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import NeighbourhoodSection from "../NeighbourhoodSection";
import useOverviewGalleryArm from "../OverviewGallery/hooks/useOverviewGalleryArm";
import overviewLayout from "../OverviewGallery/overviewLayout.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

const SectionInternal = loadable(() => import("../AgentView/SectionInternal"), {
  fallback: <FullPageLoader />,
});
const MapLocation = lazy(() => import("../MapSection"));

const LeftSection = ({ isLoading = false, isMapSectionV2 = false, isAgentView = false }) => {
  const { isWhitelabel, isUniversityPartner, isAllowedEmail, partner_id } = useWhitelabel();
  const isBookFormEnabledForInactiveProperty = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_BOOK_FORM_FOR_INACTIVE_PROPERTY,
  );
  // Hero gallery experiment (see the hook): this fold is dropped once the new fold is the one
  // showing.
  const { variant: overviewVariant, mode } = useOverviewGalleryArm();
  const isOverviewSplit = overviewVariant === "new";

  // Only the whitelabel flags — don't subscribe to the full inventory object.
  const {
    isWhitelabelInventory,
    whitelabelPartnerIds,
    hasParent,
    parentIsWhitelabelInventory,
    parentWhitelabelPartnerIds,
  } = useSelector(({ propertyPageData }: AppState) => {
    const data = propertyPageData?.data;
    const parent = data?.parent;
    return {
      isWhitelabelInventory: Boolean(data?.is_whitelabel_inventory),
      whitelabelPartnerIds: data?.whitelabel_partner_ids ?? null,
      hasParent: Boolean(parent),
      parentIsWhitelabelInventory: Boolean(parent?.is_whitelabel_inventory),
      parentWhitelabelPartnerIds: parent?.whitelabel_partner_ids ?? null,
    };
  }, shallowEqual);

  // Hide offers section for whitelabel-only inventories on specific partners (UAL, Blusky)
  const shouldHideOffers =
    isWhitelabel &&
    isWhitelabelPartnerInventoryFunc(
      hasParent
        ? {
            parent: {
              is_whitelabel_inventory: parentIsWhitelabelInventory,
              whitelabel_partner_ids: parentWhitelabelPartnerIds,
            },
          }
        : {
            is_whitelabel_inventory: isWhitelabelInventory,
            whitelabel_partner_ids: whitelabelPartnerIds,
          },
      partner_id,
    ) &&
    HIDE_OFFERS_FOR_WL_INVENTORY_PARTNER_IDS.includes(Number(partner_id));

  // Generate question set for the Smart Question section
  const [smartQuestionsSet1, smartQuestionsSet2] = useMemo(() => getTwoQuestionSets(), []);

  // One tree for loading and loaded, as on mobile: a separate loading tree remounted the
  // gallery, top section and offers once the property arrived.
  return (
    <RoomTypeContextProvider isLoading={isLoading}>
      <div className={styles.container}>
        {/* PropertyPageDesktop renders the top fold full width above the columns, so the left
            column starts at Offers. For the new variant both folds are rendered until mount and
            CSS hides this one at ≥1200. While loading, this fold always shows, without the
            experiment's styling, as it did when loading had a tree of its own. */}
        {(isLoading || mode !== "new") && (
          <div
            className={formatClassNames(
              styles.section,
              !isLoading && isOverviewSplit && overviewLayout.oldOnly,
            )}
          >
            <SectionFeaturedDesktop
              isLoading={isLoading}
              isBookFormEnabledForInactiveProperty={isBookFormEnabledForInactiveProperty}
              isOverviewSplit={!isLoading && isOverviewSplit}
            />
            <PropertyTopSection isDesktop loading={isLoading} />
          </div>
        )}
        {!isLoading && isAgentView && <SectionInternal />}
        {!isLoading && <SimilarPropertiesWrapper isTopSection />}
        {(!isWhitelabel || isUniversityPartner || isAllowedEmail) && !shouldHideOffers && (
          <OffersSection isDesktop isAgentView={isAgentView} isLoading={isLoading} />
        )}
        {!isLoading && (
          <>
            <PropertyAbout isDesktop />
            <SmartQuestions questions={smartQuestionsSet1} />
            <VideoSection />
            <AmenitiesSection isDesktop />
            <RoomTypeSectionDynamicDesktop isDesktop />
            <NextRoomButton />
            <HouseRules isDesktop />
            <PaymentAndFees isDesktop />
            <FloorPlansSection isDesktop />
            <NeighbourhoodSection />
            <MeetTheResidents isDesktop />
            <SectionRegionInfo
              includeFields={["inventoryFaqs"]}
              isDesktop
              defaultFirstOpen
              smartQuestions={smartQuestionsSet2}
            />

            {!isMapSectionV2 && (
              <>
                <LocationProvider>
                  <LazyLoadOnScroll fallback={<section style={{ height: "340px" }} id="map" />}>
                    <MapLocation isDesktop />
                  </LazyLoadOnScroll>
                </LocationProvider>
                <VirtualTourConnectSection />
              </>
            )}
          </>
        )}
      </div>
    </RoomTypeContextProvider>
  );
};

export default wrapperHOC(LeftSection, {
  componentName: "LeftSectionDesktop-PropertyPage",
  showForChina: true,
});
